import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type UndoAction = {
  type: 'save' | 'edit' | 'delete';
  expense: any;
  previousData?: any;
};

export const useUndoStack = (onUpdate: () => void) => {
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const { toast, dismiss } = useToast();

  const pushUndo = useCallback((action: UndoAction) => {
    setUndoStack(prev => [...prev.slice(-4), action]); // Keep last 5
  }, []);

  const undo = useCallback(async (toastId?: string) => {
    if (undoStack.length === 0) return;
    
    const action = undoStack[undoStack.length - 1];
    
    try {
      if (action.type === 'save') {
        // Delete the expense
        await supabase.from('expenses').delete().eq('id', action.expense.id);
      } else if (action.type === 'delete') {
        // Re-insert the expense
        await supabase.from('expenses').insert(action.expense);
      } else if (action.type === 'edit') {
        // Restore previous data
        await supabase.from('expenses')
          .update(action.previousData)
          .eq('id', action.expense.id);
      }

      if (toastId) dismiss(toastId);
      setUndoStack(prev => prev.slice(0, -1));
      onUpdate();
      
      toast({
        title: "Undone",
        duration: 2000,
      });
    } catch (error) {
      console.error('Undo error:', error);
      toast({
        title: "Undo failed",
        variant: "destructive",
      });
    }
  }, [undoStack, onUpdate, toast, dismiss]);

  return { pushUndo, undo };
};
